import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    CloudAppSecuritySessionControlType,
    ContinuousAccessEvaluationMode,
    SignInFrequencyAuthenticationType,
    SignInFrequencyInterval,
    SigninFrequencyType,
} from '@microsoft/microsoft-graph-types-beta';
import { SessionControlsControllerValues } from '../../../cap-generic-form';

export interface CapSessionControlDialogData {
    session: SessionControlsControllerValues;
}

interface SignInFrequency {
    // value: number,
    type: SigninFrequencyType;
    frequencyInterval: SignInFrequencyInterval;
    isEnabled: boolean;
    // This property isn't required when using frequencyInterval with the value of timeBased.
    authenticationType: SignInFrequencyAuthenticationType;
    value: FormControl;
}

type SessionControllerValue = string;

interface Controllers {
    name: string;
    value: SessionControllerValue;
    checked: boolean;
    disabled: boolean;
}

interface CloudAppSecurity {
    cloudAppSecurityType?: CloudAppSecuritySessionControlType;
}

interface ContinuousAccessEvaluation {
    mode: ContinuousAccessEvaluationMode;
}

interface PersistentBrowser {
    mode: 'never' | 'always';
}

interface Controls {
    signInFrequency: SignInFrequency;
    cloudAppSecurity: CloudAppSecurity;
    continuousAccessEvaluation: ContinuousAccessEvaluation;
    persistentBrowser: PersistentBrowser;
}

// const sessionControllers: Controllers[] = [
//     // { name: 'Use app enforced restrictions', value: '', checked: false, disabled: false },
//     // { name: 'Use Global Secure Access security profile', value: '', checked: false, disabled: false },

//     { name: 'Use Conditional Access App Control', value: 'cloudAppSecurity', checked: false, disabled: false },
//     { name: 'Sign-in frequency', value: 'signInFrequency', checked: false, disabled: false },
//     // { name: 'Persistent browser session', value: 'd', checked: false, disabled: false },
//     { name: 'Customize continuous access evaluation', value: 'continuousAccessEvaluation', checked: false, disabled: false },
//     { name: 'Disable resilience defaults', value: 'disableResilienceDefaults', checked: false, disabled: false },
//     // { name: 'Require token protection for sign-in sessions (Preview)', value: 'd', checked: false, disabled: false },
// ];

@Component({
    selector: 'app-cap-session-control-dialog',
    templateUrl: './cap-session-control-dialog.component.html',
    styleUrls: ['./cap-session-control-dialog.component.scss'],
})
export class CapSessionControlDialogComponent implements OnInit {
    sessionControllers: Controllers[] = [];

    controls: Controls;
    error: string;
    sessionValues: SessionControlsControllerValues; // initial values;

    constructor(
        private fb: FormBuilder,
        @Inject(MAT_DIALOG_DATA) public data: CapSessionControlDialogData,
        public dialog: MatDialogRef<CapSessionControlDialogComponent>,
    ) {
        this.sessionValues = this.data.session;
    }

    ngOnInit(): void {
        const singInFrequencyType = this.sessionValues.signInFrequency.type || 'hours';
        const frequencyInterval: SignInFrequencyInterval =
            this.sessionValues.signInFrequency.frequencyInterval || 'everyTime';

        const frequencyLimits = {
            days: { min: 1, max: 365 },
            hours: { min: 1, max: 23 },
        };

        const { min, max } = frequencyLimits[singInFrequencyType] || { min: 1, max: 23 };
        const signInFrequencyValueValidators =
            frequencyInterval === 'timeBased' ? [(Validators.required, Validators.min(min), Validators.max(max))] : [];

        this.controls = {
            signInFrequency: {
                value: this.fb.control(this.sessionValues.signInFrequency.value, signInFrequencyValueValidators), // 23 if type is days
                type: singInFrequencyType,
                frequencyInterval,
                isEnabled: this.sessionValues.signInFrequency.isEnabled,
                authenticationType:
                    this.sessionValues.signInFrequency.authenticationType || 'primaryAndSecondaryAuthentication',
            },

            cloudAppSecurity: {
                cloudAppSecurityType: this.sessionValues.cloudAppSecurity.cloudAppSecurityType || 'monitorOnly', // Default,
            },

            continuousAccessEvaluation: {
                mode: this.sessionValues.continuousAccessEvaluation.mode || 'strictLocation', // Default
            },
            persistentBrowser: {
                mode: this.sessionValues.persistentBrowser.mode || 'never',
            },
        };

        this.sessionControllers = [
            {
                name: 'Use Conditional Access App Control',
                value: 'cloudAppSecurity',
                checked: this.sessionValues.cloudAppSecurity.isEnabled,
                disabled: false,
            },
            {
                name: 'Sign-in frequency',
                value: 'signInFrequency',
                checked: this.sessionValues.signInFrequency.isEnabled,
                disabled: false,
            },
            {
                name: 'Persistent browser session',
                value: 'persistentBrowser',
                checked: this.sessionValues.persistentBrowser.isEnabled,
                disabled: false,
            },
            {
                name: 'Customize continuous access evaluation',
                value: 'continuousAccessEvaluation',
                checked: this.sessionValues.continuousAccessEvaluation.mode ? true : false,
                disabled: false,
            },
            {
                name: 'Disable resilience defaults',
                value: 'disableResilienceDefaults',
                checked: this.sessionValues.disableResilienceDefaults,
                disabled: false,
            },
        ];
    }

    save() {
        this.setError();
        if (this.error) return;
        const disableResilienceDefaultsEnabled = this.sessionControllers.find(
            (res) => res.value === 'disableResilienceDefaults',
        )?.checked;
        const signInFrequencyEnabled = this.sessionControllers.find((res) => res.value === 'signInFrequency')?.checked;
        const continuousAccessEvaluationEnable = this.sessionControllers.find(
            (res) => res.value === 'continuousAccessEvaluation',
        )?.checked;

        const cloudAppSecurityEnabled = this.sessionControllers.find(
            (res) => res.value === 'cloudAppSecurity',
        )?.checked;

        const persistentBrowserEnabled = this.sessionControllers.find(
            (res) => res.value === 'persistentBrowser',
        )?.checked;

        let signInFrequency = {
            ...this.controls.signInFrequency,
            value: this.controls.signInFrequency.value.value,
            isEnabled: signInFrequencyEnabled,
        };

        if (this.controls.signInFrequency.frequencyInterval === 'everyTime') {
            signInFrequency = {
                value: undefined,
                isEnabled: signInFrequencyEnabled,
                type: undefined,
                frequencyInterval: 'everyTime',
                authenticationType: undefined,
            };
        }

        const sessionValue: SessionControlsControllerValues = {
            signInFrequency,

            continuousAccessEvaluation: {
                mode: continuousAccessEvaluationEnable ? this.controls.continuousAccessEvaluation.mode : undefined,
            },

            cloudAppSecurity: {
                cloudAppSecurityType: this.controls.cloudAppSecurity.cloudAppSecurityType,
                isEnabled: cloudAppSecurityEnabled,
            },

            persistentBrowser: {
                isEnabled: persistentBrowserEnabled,
                mode: this.controls.persistentBrowser.mode,
            },

            disableResilienceDefaults: disableResilienceDefaultsEnabled,
        };

        console.log(sessionValue);

        this.dialog.close(sessionValue);
    }

    onFrequencyIntervalChange() {
        if (this.controls.signInFrequency.frequencyInterval === 'everyTime') {
            this.controls.signInFrequency.value.clearValidators();
            this.controls.signInFrequency.value.updateValueAndValidity();
        } else {
            this.onSignInFrequencyTypeChange(this.controls.signInFrequency.type);
        }
        this.setError();
    }

    onSignInFrequencyTypeChange(type: SigninFrequencyType) {
        this.controls.signInFrequency.value.clearValidators();
        if (type === 'hours') {
            this.controls.signInFrequency.value.setValidators([
                Validators.required,
                Validators.min(1),
                Validators.max(23),
            ]);
        } else {
            this.controls.signInFrequency.value.setValidators([
                Validators.required,
                Validators.min(1),
                Validators.max(365),
            ]);
        }
        // order maters, this one should be at the end
        this.controls.signInFrequency.value.updateValueAndValidity();
        this.setError();
    }

    setError() {
        const signInFrequencyEnabled = this.sessionControllers.find((res) => res.value === 'signInFrequency')?.checked;
        this.error = null;

        if (!signInFrequencyEnabled) {
            return null;
        }

        if (this.controls.signInFrequency.type === null) {
            this.error = 'Please select a frequency type';
            return true;
        }

        if (
            this.controls.signInFrequency.value.invalid &&
            this.controls.signInFrequency.frequencyInterval === 'timeBased'
        ) {
            this.error = 'Please enter a valid Sing-in frequency value';
            return true;
        }

        return false;
    }
}
